import './styles.scss'

import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { Typography } from 'antd'

import CustomButton from '@/components/CustomButton'
import { ChevronLeftIcon } from '@/components/Icons'
import { NotificationManager } from '@/components/Notification'
import { fapiCustomerService } from '@/services/fapi'
import { useAppSelector } from '@/store/hook'
import { selectRegions } from '@/store/selectors/configSelector'
import useAdobeAnalysis from '@/hooks/useAdobeAnalysis'
import useAsync from '@/hooks/useAsync'
import useDocumentTitle from '@/hooks/useDocumentTitle'
import { PAGE_TYPES } from '@/libs/constants/adobeAnalytics'
import { linkGenerator } from '@/libs/utils/language'
import { isFormDirty } from '@/libs/utils/validatorUtils'

import InvoiceChangeForm from './components/InvoiceChangeForm'

const ProfileChangeInvoicePage = () => {
  useDocumentTitle('changeInvoiceAddress.title')

  useAdobeAnalysis(null, {
    pageId: '18',
    pageType: PAGE_TYPES.PROFILE,
    pageName: 'change invoice address',
  })

  const { t } = useTranslation()
  const navigate = useNavigate()
  const [formValues, setFormValues] = useState({})
  const regions = useAppSelector(selectRegions)

  const IS_NOT_DIRTY_MESSAGE = 'IS_NOT_DIRTY_MESSAGE'

  const submitInvoiceChange = useCallback(
    async (parameters) => {
      const isDirty = isFormDirty(formValues, parameters)
      if (!isDirty) {
        throw new Error(IS_NOT_DIRTY_MESSAGE)
      }
      await fapiCustomerService.submitInvoiceChange(parameters)
      setFormValues(parameters)
    },
    [formValues],
  )

  const { execute, status, error } = useAsync(submitInvoiceChange, false)

  const handleRequestInvoiceChange = (values) => {
    const bodyValues = values
    if (bodyValues.region && regions[bodyValues.region]) {
      bodyValues.region = regions[bodyValues.region].label
    }

    execute(bodyValues).then(({ error: error_ }) => {
      window.scrollTo({ top: 0 })

      if (error_ && error_.message !== IS_NOT_DIRTY_MESSAGE) {
        NotificationManager.error({
          message: `checkout.${error_?.response?.data}`,
        })
        return
      }

      NotificationManager.success({
        message: 'changeInvoiceAddress.successfullMessage',
      })
    })
  }

  const isLoading = status === 'pending'
  const isDirty = error?.message !== IS_NOT_DIRTY_MESSAGE
  const isError = status === 'error' && isDirty

  return (
    <div className="b2bec-change-invoice">
      <div className="b2bec-change-invoice__header">
        <h1>{t('changeInvoiceAddress.title')}</h1>

        <CustomButton
          buttonSize="small"
          className="back-btn"
          onClick={() => navigate(linkGenerator('/profile'))}
        >
          <ChevronLeftIcon />
          {t('searchResultsPage.button.backToOverview')}
        </CustomButton>
      </div>

      <div className="b2bec-change-invoice__description">
        {t('changeInvoiceAddress.description')}
      </div>

      {isError
        ? (
          <Typography.Text
            className="b2bec-change-invoice__message"
            type="danger"
          >
            {t('changeInvoiceAddress.errorMessage')}
          </Typography.Text>
        )
        : null}

      <InvoiceChangeForm
        disabled={isLoading}
        isSubmiting={isLoading}
        onFinish={handleRequestInvoiceChange}
      />
    </div>
  )
}

export default ProfileChangeInvoicePage
